/**
 * The orders a list offers, and sorting by one of them.
 *
 * Two kinds of list share this. A paged list sends the order upstream (`sortParamsOf`), because
 * sorting the page on screen would call «el más antiguo» the oldest of twenty-four. A list that
 * arrives whole sorts itself (`sortRows`), and there the rules that can be wrong without anybody
 * noticing are the ones written down here: where the rows with nothing to sort by go, and how a
 * name with an accent or a number in it compares.
 */
import type { SortDir } from '@beonit/adaptive-raindrop';

export interface SortOption<F extends string = string> {
  /** `field:direction`, the value a menu can hold. */
  key: string;
  label: string;
  orderBy: F;
  orderDir: SortDir;
}

/**
 * The option a key names, or the first — the default — for a key nobody offered.
 *
 * A key comes from somewhere that can go stale (a URL, a stored preference), and an unknown one is
 * better read as «the usual order» than as an error on a screen that only wanted to list things.
 */
export function sortOptionOf<F extends string>(
  options: readonly SortOption<F>[],
  key: string,
): SortOption<F> {
  const option = options.find((candidate) => candidate.key === key) ?? options[0];
  // An empty list of orders is a mistake in the code, not something a person did: say so loudly.
  if (!option) throw new Error('sortOptionOf: a list has to offer at least one order');

  return option;
}

/** The list params of an order, or the default's for a key nobody offered. */
export function sortParamsOf<F extends string>(
  options: readonly SortOption<F>[],
  key: string,
): { orderBy: F; orderDir: SortDir } {
  const option = sortOptionOf(options, key);

  return { orderBy: option.orderBy, orderDir: option.orderDir };
}

/**
 * Spanish order, not code-point order: «Álvaro» sits with the A's, case does not split a list in
 * two, and «vídeo 10» comes after «vídeo 9».
 */
const COLLATOR = new Intl.Collator('es', { sensitivity: 'base', numeric: true });

/** A value reduced to something comparable; `null` is «nothing to sort by». */
type Comparable = string | number | null;

function comparable(value: string | number | Date | null | undefined): Comparable {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) value = value.getTime();
  // An invalid date or a NaN has no place in an order either: it is missing, not «smallest».
  if (typeof value === 'number') return Number.isNaN(value) ? null : value;

  return value;
}

/** Ascending order of two present values. */
function ascending(a: string | number, b: string | number): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;

  return COLLATOR.compare(String(a), String(b));
}

/**
 * The rows in the option's order, as a new array.
 *
 * Rows with nothing to sort by go last whichever the direction: turning the order round should
 * bring the other end of the list to the top, not a block of blanks. Stable, so rows that tie keep
 * the order they came in — in either direction — and re-sorting a list does not shuffle it.
 */
export function sortRows<T, F extends string>(
  rows: readonly T[],
  option: SortOption<F>,
  value: (row: T, field: F) => string | number | Date | null | undefined,
): T[] {
  const sign = option.orderDir === 'desc' ? -1 : 1;
  // Each row's value read once, not once per comparison.
  const keyed = rows.map((row) => ({ row, key: comparable(value(row, option.orderBy)) }));

  keyed.sort((a, b) => {
    if (a.key === null) return b.key === null ? 0 : 1;
    if (b.key === null) return -1;

    return sign * ascending(a.key, b.key);
  });

  return keyed.map((entry) => entry.row);
}
